import { Edit2, Pause, Play, SquareArrowOutUpRight, Zap } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Context } from '../../../../components/root/context';
import useApiRequest from '../../../../lib/hooks/useRequest';
import { convertHtmlToEditorState } from '../../../../lib/hooks/utils';
import { JobDetailsCustomerResponseDto, JobStatus } from '../../../../lib/interfaces/customers/jobs';
import { Button } from '../../../../ui/components/ui/button';
import DataFetchParent from '../../../../ui/components/ui/data-fetch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/components/ui/select';
import { PausedOrder, StatusBar } from '../../[id]/page';
import Chat from '../../chat';
import CreateEditor from '../../create/create-editor';
import DataUpload from '../../create/data-upload';
import JobFileList from '../../file-list';

export const JobDetailsParent = ({ id }: { id: string }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState<JobDetailsCustomerResponseDto>();
  const [reload, setReload] = useState(false);
  const { apiRequest } = useApiRequest();

  const fetchOrder = useCallback(async () => {
    setIsLoading(true);
    const res = await apiRequest<JobDetailsCustomerResponseDto>(`jobs/customer/${id}`, 'GET');
    setIsLoading(false);
    if (res.data) {
      setOrder(res.data);
    }
  }, [apiRequest, id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder, reload]);

  if (!id) {
    return <div>Organisation not found</div>;
  }

  return (
    <DataFetchParent data={order} renderElement={(data) => <OrderDetails orderId={id} setReload={setReload} order={data} />} isLoading={isLoading} />
  );
};

function OrderDetails({
  order,
  orderId,
  setReload
}: {
  order: JobDetailsCustomerResponseDto;
  orderId: string;
  setReload: Dispatch<SetStateAction<boolean>>;
}) {
  const { apiRequest } = useApiRequest();
  const [editorState] = useState(() => convertHtmlToEditorState(order.description));
  const { isMobile } = useContext(Context);
  const [editContactHover, setEditContactHover] = useState(false);
  const [editContact, setEditContact] = useState(false);

  const reactivateOrder = async () => {
    const res = await apiRequest(`jobs/customer/${orderId}/reactivate`, 'PATCH');
    if (res.data) {
      setReload((prev) => !prev);
    }
  };

  const togglePausedOrder = async () => {
    let url = order.status === JobStatus.OnHold ? `jobs/customer/${orderId}/resume` : `jobs/customer/${orderId}/pause`;
    const res = await apiRequest(url, 'PATCH');
    if (res.data) {
      setReload((prev) => !prev);
    }
  };

  const changeContact = async (newUserId: string) => {
    const res = await apiRequest(`jobs/customer/${orderId}/belongs-to-customer-user`, 'PATCH', {
      body: {
        belongsToCustomerUserId: newUserId
      },
      toast: {
        toastText: 'Ansprechpartner geändert'
      }
    });
    if (res.data) {
      setReload((prev) => !prev);
    }
    setEditContact(false);
  };

  return (
    <div className="flex flex-col gap-3 h-full">
      <div className="flex justify-between">
        <div className={`flex justify-between grow ${isMobile ? 'flex-col gap-2' : ''}`}>
          <div className="grow">
            <div className={`flex gap-4 items-center ${isMobile ? 'flex-col gap-2' : ''}`}>
              <h1 className="text-[32px] leading-[38px] font-bold text-[#12282A]">
                {order.jobIncrementId} {order.title}
              </h1>
            </div>
            {editContact ? (
              <div className="w-fit">
                <Select
                  onValueChange={(value) => {
                    changeContact(value);
                  }}
                  defaultValue={order.selectableEntities.customerUsers.find((user) => user.value === order.belongsToCustomerUserFullName)?._id}>
                  <SelectTrigger className="gap-3 h-10 rounded-2xl px-3 py-2 border text-sm inline-flex justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground">
                    <SelectValue placeholder="Ansprechpartner auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {order.selectableEntities.customerUsers.map((user) => (
                      <SelectItem key={user._id} value={user._id}>
                        {user.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div
                className="flex items-center text-[#888] w-fit"
                onMouseEnter={() => setEditContactHover(true)}
                onMouseLeave={() => setEditContactHover(false)}>
                <p className="text-[16px]">Ansprechpartner: {order.belongsToCustomerUserFullName}</p>
                <Button variant="ghost" size="icon" className={editContactHover ? 'opacity-100' : 'opacity-75'} onClick={() => setEditContact(true)}>
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {order.status === JobStatus.OnHold && <PausedOrder />}
          {order.dropboxUrl && (
            <Button
              type="button"
              onClick={() => window.open(order.dropboxUrl, '_blank')}
              className={`flex gap-4 ${order.selectableEntities.reactivateEnabled ? 'mr-4' : ''}`}>
              Dropbox öffnen
            </Button>
          )}
          {(order.status === JobStatus.OnHold || order.status === JobStatus.Active || order.status === JobStatus.Feedback) && (
            <Button className="flex gap-4" onClick={togglePausedOrder}>
              {order.status === JobStatus.OnHold ? <Play /> : <Pause />}
              <span>{order.status === JobStatus.OnHold ? 'Auftrag fortsetzen' : 'Auftrag pausieren'}</span>
            </Button>
          )}
          {order.selectableEntities.reactivateEnabled && (
            <Button className="flex gap-4" onClick={reactivateOrder}>
              <Zap />
              <span>Auftrag reaktivieren</span>
            </Button>
          )}
        </div>
      </div>
      {order.status !== JobStatus.OnHold && <StatusBar status={order.status} />}
      <div className={`flex gap-6 ${isMobile ? 'flex-col' : 'h-[70vh]'}`}>
        <div className={`flex flex-col ${isMobile ? 'w-full' : 'w-1/2'} gap-4 h-full`}>
          {order.finalUrl && (
            <Link to={order.finalUrl} className="flex flex-col bg-[#12282A] rounded-[14px] p-6 gap-2" target="_blank">
              <p className="text-white font-bold">Link zu den finalen Dateien</p>
              <div className="bg-white px-5 py-2 rounded-2xl flex items-center justify-between">
                <input type="text" defaultValue={order.finalUrl} className="font-semibold outline-none w-full" />
                <SquareArrowOutUpRight />
              </div>
            </Link>
          )}
          <div className="bg-white rounded-[14px] py-3 pr-3 flex-grow overflow-hidden">
            <div className="flex flex-col p-6 gap-3 custom-scrollbar h-full overflow-auto">
              <p className="text-[#12282A] font-bold">Auftragsbeschreibung</p>
              <div className="bg-white mb-8 rounded-2xl flex items-center justify-betweentext-[#888]">
                <div className="w-full resize-none custom-scrollbar pr-2 outline-none disabled:bg-white">
                  <CreateEditor disabled={true} editorState={editorState} />
                </div>
              </div>
              <JobFileList files={order.files} id={orderId} type="customer" setReload={setReload} />
            </div>
          </div>
        </div>
        <Chat type="customer" id={orderId} setReload={setReload} />
      </div>
      {order.filesAllowed && <DataUpload type="customer" onFilesUploaded={() => setReload((prev) => !prev)} id={orderId} withUploadButton={true} remainingFileSize={order.maxUploadSize} />}
    </div>
  );
}
